import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Test, Question } from '../../types/test';
import Modal from '../../components/Modal';
import './TakeTest.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const TakeTest: React.FC = () => {
  const { testId } = useParams<{ testId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [test, setTest] = useState<Test | null>(null);
  const [answers, setAnswers] = useState<Record<string, string | string[]>>({});
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  
  // Modal state
  const [modal, setModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'success' | 'error' | 'info' | 'warning';
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'info',
  });

  useEffect(() => {
    if (testId) {
      fetchTest();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testId]);

  const fetchTest = async () => {
    try {
      const response = await fetch(`${API_URL}/tests/${testId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        const testData = data.test;
        
        // Check if test is past due
        if (testData.dueDate) {
          const dueDate = new Date(testData.dueDate);
          const now = new Date();
          if (dueDate < now) {
            setModal({
              isOpen: true,
              title: 'Test Past Due',
              message: 'This test is past its due date and can no longer be taken.',
              type: 'error',
            });
            setLoading(false);
            return;
          }
        }
        
        setTest(testData);
        // Initialize answers object
        const initialAnswers: Record<string, string | string[]> = {};
        testData.questions.forEach((q: Question) => {
          if (q.type === 'multiple-choice') {
            initialAnswers[q.id] = '';
          } else if (q.type === 'true-false') {
            initialAnswers[q.id] = '';
          } else {
            initialAnswers[q.id] = '';
          }
        });
        setAnswers(initialAnswers);
      }
    } catch (error) {
      console.error('Error fetching test:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionId: string, value: string | string[]) => {
    setAnswers({
      ...answers,
      [questionId]: value,
    });
  };

  const handleNext = () => {
    if (test && currentQuestion < test.questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleSubmitClick = () => {
    if (!test || !user) return;
    setShowConfirmModal(true);
  };

  const handleSubmitConfirm = async () => {
    if (!test || !user) return;

    // Check if test is past due before submitting
    if (test.dueDate) {
      const dueDate = new Date(test.dueDate);
      const now = new Date();
      if (dueDate < now) {
        setShowConfirmModal(false);
        setModal({
          isOpen: true,
          title: 'Test Past Due',
          message: 'This test is past its due date and can no longer be submitted.',
          type: 'error',
        });
        return;
      }
    }

    setShowConfirmModal(false);
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/tests/${testId}/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
        },
        body: JSON.stringify({
          employeeId: user.id,
          employeeName: user.name,
          employeeEmail: user.email,
          answers: Object.entries(answers).map(([questionId, answer]) => ({
            questionId,
            answer,
          })),
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setResult(data.result);
        setSubmitted(true);
      } else {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to submit test' }));
        setModal({
          isOpen: true,
          title: 'Error',
          message: errorData.detail || 'Failed to submit test. Please try again.',
          type: 'error',
        });
      }
    } catch (error) {
      console.error('Error submitting test:', error);
      setModal({
        isOpen: true,
        title: 'Error',
        message: 'Failed to submit test. Please try again.',
        type: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="take-test">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading test...</p>
        </div>
      </div>
    );
  }

  if (!test) {
    return (
      <div className="take-test">
        <div className="error-container">
          <p>Test not found</p>
          <button onClick={() => navigate('/employee/dashboard')}>Back to Dashboard</button>
        </div>
      </div>
    );
  }

  if (submitted && result) {
    return (
      <div className="take-test">
        <Modal
          isOpen={modal.isOpen}
          onClose={() => setModal({ ...modal, isOpen: false })}
          title={modal.title}
          message={modal.message}
          type={modal.type}
        />
        <div className="test-complete">
          <div className="complete-icon">✅</div>
          <h1>Test Submitted!</h1>
          <div className="score-display">
            <div className="score-circle">
              <span className="score-value">{result.percentage.toFixed(1)}%</span>
              <span className="score-label">Score</span>
            </div>
            <div className="score-details">
              <p><strong>{result.score}</strong> out of <strong>{result.totalPoints}</strong> points</p>
              <p className="score-message">
                {result.percentage >= 80 ? '🎉 Excellent work!' : 
                 result.percentage >= 60 ? '👍 Good job!' : 
                 '📚 Keep studying!'}
              </p>
            </div>
          </div>
          <div className="complete-actions">
            <button className="button-primary" onClick={() => navigate('/employee/dashboard')}>
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  const question = test.questions[currentQuestion];
  const progress = ((currentQuestion + 1) / test.questions.length) * 100;
  const allAnswered = test.questions.every((q) => {
    const answer = answers[q.id];
    return answer !== undefined && answer !== '' && 
           (Array.isArray(answer) ? answer.length > 0 : true);
  });

  return (
    <div className="take-test">
      <Modal
        isOpen={modal.isOpen}
        onClose={() => setModal({ ...modal, isOpen: false })}
        title={modal.title}
        message={modal.message}
        type={modal.type}
      />
      
      <Modal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        title="Confirm Submission"
        message="Are you sure you want to submit your test? You cannot change your answers after submitting."
        type="warning"
        showCancel={true}
        confirmText="Submit"
        onConfirm={handleSubmitConfirm}
      />
      <div className="test-header">
        <button className="button-back" onClick={() => navigate('/employee/dashboard')}>
          ← Back to Tests
        </button>
        <div className="test-info">
          <h1>{test.title}</h1>
          <p>{test.description}</p>
        </div>
      </div>

      <div className="progress-bar">
        <div className="progress-fill" style={{ width: `${progress}%` }}></div>
        <span className="progress-text">
          Question {currentQuestion + 1} of {test.questions.length}
        </span>
      </div>

      <div className="question-container">
        <div className="question-header">
          <span className="question-number">Q{currentQuestion + 1}</span>
          <span className="question-points">{question.points} points</span>
        </div>

        <h2 className="question-text">{question.questionText}</h2>

        <div className="answer-section">
          {question.type === 'multiple-choice' && question.options && (
            <div className="options-list">
              {question.options.map((option, index) => (
                <label key={index} className="option-label">
                  <input
                    type="radio"
                    name={`question-${question.id}`}
                    value={option}
                    checked={answers[question.id] === option}
                    onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                  />
                  <span>{option}</span>
                </label>
              ))}
            </div>
          )}

          {question.type === 'true-false' && (
            <div className="options-list">
              <label className="option-label">
                <input
                  type="radio"
                  name={`question-${question.id}`}
                  value="True"
                  checked={answers[question.id] === 'True'}
                  onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                />
                <span>True</span>
              </label>
              <label className="option-label">
                <input
                  type="radio"
                  name={`question-${question.id}`}
                  value="False"
                  checked={answers[question.id] === 'False'}
                  onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                />
                <span>False</span>
              </label>
            </div>
          )}
        </div>
      </div>

      <div className="test-navigation">
        <button
          className="button-secondary"
          onClick={handlePrevious}
          disabled={currentQuestion === 0}
        >
          ← Previous
        </button>

        <div className="question-indicators">
          {test.questions.map((q, index) => (
            <button
              key={q.id}
              className={`question-indicator ${index === currentQuestion ? 'active' : ''} ${
                answers[q.id] && answers[q.id] !== '' ? 'answered' : ''
              }`}
              onClick={() => setCurrentQuestion(index)}
            >
              {index + 1}
            </button>
          ))}
        </div>

        {currentQuestion < test.questions.length - 1 ? (
          <button
            className="button-primary"
            onClick={handleNext}
          >
            Next →
          </button>
        ) : (
          <button
            className="button-primary submit-button"
            onClick={handleSubmitClick}
            disabled={!allAnswered || submitting}
          >
            {submitting ? 'Submitting...' : 'Submit Test'}
          </button>
        )}
      </div>
    </div>
  );
};

export default TakeTest;
